import { useEffect, useState } from 'react';
import { Play, Loader2, CircleCheck, AlertTriangle, AlertOctagon, ArrowDown, ArrowUp, Equal, AlertCircle, Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdminAPI } from '@/lib/admin-api-quality';
import { formatINR } from '@/lib/format';
import { cn } from '@/lib/utils';

const STATUS_BADGE = {
  pass:     { tone: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', icon: CircleCheck, label: 'pass' },
  warn:     { tone: 'bg-amber-500/10 text-amber-600 border-amber-500/30',       icon: AlertTriangle, label: 'warn' },
  critical: { tone: 'bg-rose-500/10 text-rose-600 border-rose-500/30',          icon: AlertOctagon, label: 'critical' },
  info:     { tone: 'bg-primary/10 text-primary border-primary/30',             icon: AlertCircle, label: 'info' },
  new:      { tone: 'bg-slate-500/10 text-slate-600 border-slate-500/30',       icon: Sparkles,    label: 'new' },
  error:    { tone: 'bg-rose-500/10 text-rose-600 border-rose-500/30',          icon: AlertOctagon, label: 'error' },
};

export const ParityPanel = ({ cardId, versionId, onComplete }) => {
  const [batteries, setBatteries] = useState([]);
  const [batteryId, setBatteryId] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    AdminAPI.quality.parityBatteries().then((bs) => {
      setBatteries(bs || []);
      const def = (bs || []).find((b) => b.is_default);
      if (def) setBatteryId(def.id);
    }).catch(() => {});
  }, []);

  const run = async () => {
    if (!versionId) return;
    setRunning(true);
    try {
      const r = await AdminAPI.quality.runParity(cardId, versionId, batteryId || null);
      setResult(r);
      onComplete?.(r);
    } catch (e) {
      // surface error in result panel
      setResult({ error: e.message || 'Parity run failed' });
    } finally { setRunning(false); }
  };

  const filtered = (result?.results || []).filter((r) =>
    statusFilter === 'all' ? true : r.diff?.status === statusFilter
  );

  return (
    <div data-testid="admin-parity-panel" className="flex flex-col">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">Parity battery</div>
          {result?.summary && (
            <div className="flex items-center gap-1 text-[10px]">
              <Badge variant="outline" className={cn('gap-1', STATUS_BADGE.pass.tone)}><CircleCheck className="h-3 w-3" /> {result.summary.counts.pass || 0}</Badge>
              {result.summary.counts.warn > 0 && <Badge variant="outline" className={cn('gap-1', STATUS_BADGE.warn.tone)}><AlertTriangle className="h-3 w-3" /> {result.summary.counts.warn}</Badge>}
              {result.summary.counts.critical > 0 && <Badge variant="outline" className={cn('gap-1', STATUS_BADGE.critical.tone)}><AlertOctagon className="h-3 w-3" /> {result.summary.counts.critical}</Badge>}
              {result.summary.counts.new > 0 && <Badge variant="outline" className={cn('gap-1', STATUS_BADGE.new.tone)}>new {result.summary.counts.new}</Badge>}
              <span className="text-muted-foreground">· {result.summary.pass_rate}% pass</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={batteryId || ''} onValueChange={setBatteryId}>
            <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="admin-parity-battery-select"><SelectValue placeholder="Pick battery" /></SelectTrigger>
            <SelectContent>{batteries.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} v{b.version}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" className="gap-1.5" onClick={run} disabled={running || !versionId} data-testid="admin-parity-run">
            {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
            Run
          </Button>
        </div>
      </div>
      {!result ? (
        <Card className="p-6 text-center text-xs text-muted-foreground border-dashed" data-testid="admin-parity-empty">
          Run the battery to compare this draft against the active version across {batteries[0]?.scenarios?.length || '40+'} standard scenarios.
        </Card>
      ) : result.error ? (
        <Card className="p-3 border-rose-500/30 bg-rose-500/5 text-xs text-rose-700 dark:text-rose-300">{result.error}</Card>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Filter</div>
            {['all', 'pass', 'warn', 'critical', 'info', 'new'].map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} className={cn('text-[10px] uppercase tracking-wide rounded-full border px-2 py-0.5', statusFilter === s ? 'bg-foreground/10 border-foreground/30' : 'border-border text-muted-foreground hover:text-foreground')} data-testid={`admin-parity-filter-${s}`}>{s}</button>
            ))}
          </div>
          <Card className="p-1 flex-1 overflow-hidden">
            <div
              className="max-h-[460px] overflow-y-auto scrollbar-thin"
              data-testid="admin-parity-results-scroll"
            >
              <table className="w-full text-[11px]" data-testid="admin-parity-results-table">
                <thead className="text-[10px] uppercase tracking-wide text-muted-foreground bg-[hsl(var(--surface-2))]">
                  <tr><th className="text-left px-2 py-1 w-[5%]"></th><th className="text-left px-2 py-1">Scenario</th><th className="text-left px-2 py-1">Fired</th><th className="text-right px-2 py-1">Reward</th><th className="text-right px-2 py-1">Rate</th><th className="text-right px-2 py-1">Δ reward</th><th className="text-right px-2 py-1">Δ rate</th></tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const sc = r.scenario;
                    const status = r.diff?.status || 'pass';
                    const cfg = STATUS_BADGE[status] || STATUS_BADGE.pass;
                    const Icon = cfg.icon;
                    const dRew = r.diff?.reward_delta_inr || 0;
                    const dRate = r.diff?.rate_delta_pct || 0;
                    return (
                      <tr key={i} className="border-t border-border/40 hover:bg-muted/20" data-testid={`admin-parity-row-${i}`}>
                        <td className="px-2 py-1.5">
                          <Icon className={cn('h-3 w-3', cfg.tone.split(' ')[1])} />
                        </td>
                        <td className="px-2 py-1.5 min-w-0">
                          <div className="truncate font-medium">{sc.name}</div>
                          <div className="text-[9px] text-muted-foreground truncate">{(sc.tags || []).join(' · ') || '—'}</div>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1.5">
                            {r.excluded && <Badge variant="outline" className="text-[9px] bg-rose-500/10 text-rose-600 border-rose-500/30">excl</Badge>}
                            <span className="truncate">{r.fired_rule_label || '—'}</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono-num">{formatINR(r.reward_value_inr || 0)}</td>
                        <td className="px-2 py-1.5 text-right font-mono-num">{(r.effective_rate_pct || 0).toFixed(2)}%</td>
                        <td className={cn('px-2 py-1.5 text-right font-mono-num', dRew > 0 ? 'text-emerald-600' : dRew < 0 ? 'text-rose-600' : 'text-muted-foreground')}>
                          {dRew === 0 ? <Equal className="h-3 w-3 inline" /> : dRew > 0 ? <ArrowUp className="h-3 w-3 inline" /> : <ArrowDown className="h-3 w-3 inline" />}
                          {' '}{formatINR(Math.abs(dRew))}
                        </td>
                        <td className={cn('px-2 py-1.5 text-right font-mono-num', dRate > 0 ? 'text-emerald-600' : dRate < 0 ? 'text-rose-600' : 'text-muted-foreground')}>
                          {dRate > 0 ? '+' : ''}{dRate.toFixed(2)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">No scenarios match the current filter.</div>}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};
